import React, { ReactNode, Suspense, useEffect, useMemo } from 'react'
import { BrowserRouter, useLocation } from 'react-router'
import { appLoadedEvent } from '@components/Analytics/performanceAnalytics'
import { AppBugReportOverlay } from '@components/AppBugReport/AppBugReportOverlay'
import { AboveAppProgressOverlay } from '@components/AppProgressOverlay/AboveAppProgress/AboveAppProgress'
import { AppProgressOverlay } from '@components/AppProgressOverlay/AppProgressOverlay'
import { AppProgressOverlayTrigger } from '@components/AppProgressOverlay/AppProgressOverlayTrigger'
import { AppProgressState } from '@components/AppProgressOverlay/AppProgressState'
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary'
import { TownsLogoPage } from '@components/TownsLogoPage'
import { ZLayerProvider } from '@ui'
import { AppErrorFallback } from 'AppErrorFallback'
import { Analytics } from 'hooks/useAnalytics'
import { usePeriodicUpdates } from 'hooks/usePeriodicUpdates'
import { useRootTheme } from 'hooks/useRootTheme'
import { LaunchDarklyIdentify } from 'LaunchDarklyIdentify'
import { TownsPrivyProvider } from 'privy/PrivyProvider'
import { PATHS, PATHS_REGEX } from 'routes'
import { env } from 'utils'

const App = React.lazy(() => import('App'))

const DebugRouter = ({ children }: { children: ReactNode }) => {
    const location = useLocation()
    console.log(
        `[routing] ${location.pathname}${location.search}, \nState: ${JSON.stringify(
            location.state,
        )}`,
    )
    return children
}

const PrivyAndErrorBoundary = ({ children }: { children: ReactNode }) => {
    return (
        <>
            <ErrorBoundary FallbackComponent={AppErrorFallback}>
                <TownsPrivyProvider>
                    <>{children}</>
                </TownsPrivyProvider>
            </ErrorBoundary>
            <AppBugReportOverlay />
        </>
    )
}

export const Main = () => {
    useRootTheme({
        ammendHTMLBody: true,
        useDefaultOSTheme: true,
    })

    useEffect(() => {
        console.log('[analytics] Main useEffect')
        Analytics.getInstance().identify()
        appLoadedEvent()
    }, [])

    const isHomeRoute = useMemo(() => window.location.pathname === '/', [])
    const isTownPageRoute = useMemo(
        () =>
            window.location.pathname.match(
                new RegExp(`/${PATHS.SPACES}/${PATHS_REGEX.SPACE_ID}/?$`),
            ),
        [],
    )

    return (
        <PrivyAndErrorBoundary>
            {env.VITE_LAUNCHDARKLY_CLIENT_ID && <LaunchDarklyIdentify />}
            {env.VITE_ENABLE_MSW_BROWSER ? null : <PeriodicUpdates />}
            <BrowserRouter>
                <DebugRouter>
                    <Suspense
                        fallback={
                            isHomeRoute ? (
                                <TownsLogoPage />
                            ) : isTownPageRoute ? (
                                <></>
                            ) : (
                                <AppProgressOverlayTrigger
                                    progressState={AppProgressState.LoadingAssets}
                                    debugSource="Main suspense fallback"
                                />
                            )
                        }
                    >
                        <ZLayerProvider>
                            <App />
                        </ZLayerProvider>
                    </Suspense>
                </DebugRouter>
                <AppProgressOverlay />
                <AboveAppProgressOverlay />
            </BrowserRouter>
        </PrivyAndErrorBoundary>
    )
}

function PeriodicUpdates() {
    usePeriodicUpdates()
    return null
}
